import {Trans} from '@lingui/macro';
import React, {Dispatch, FC, SetStateAction, useState} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {CheckBox} from '../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {TagModel} from '../../models/api/tagModel';
import {VisualRecipientModel} from '../../models/api/visualRecipientModel';
import {emojiMap} from '../../services/emojis/emojis';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../services/tracking';
import {OkCancelDialog} from '../common/dialogs/okCancelDialog';
import {InlineEmoji} from '../common/emoji/inlineEmoji';

/*
 * Props.
 */

interface CreateTagRuleDialogProps {
  tag?: TagModel;
  recipients?: ReadonlyArray<VisualRecipientModel>;
  createRule: Handler;
  onRequestClose: Handler;
  ignoreFutureTagDrops: Handler;
}

/*
 * Style.
 */

const StyledParagraph = styled.p`
  line-height: 22px;
  margin: -3px 0px 0px;
`;

const StyledBoldSpan = styled.span`
  font-weight: bold;
`;

const StyledCheckBoxWrapperDiv = styled.div`
  margin-top: 20px;
  margin-bottom: 18px;
`;

/*
 * Component.
 */

export const CreateTagRuleDialog: FC<CreateTagRuleDialogProps> = (props) => {
  const [hideDialog, setHideDialog] = useState(false);

  if (!props.tag || !props.recipients || !props.recipients.length) {
    return null;
  }

  const onRequestClose = () => {
    if (hideDialog) {
      props.ignoreFutureTagDrops();
    }

    setHideDialog(false);
    props.onRequestClose();
  };

  const onOkClick = () => {
    trackInterfaceEvent(InterfaceEventsEnum.ADD_QUICK_RULE, {source: 'dialog'});
    props.createRule();
    onRequestClose();
  };

  return (
    <OkCancelDialog
      interactionId="dialog-create-rule"
      width={400}
      title={
        <Trans id="dialog-create-rule-title">
          Create quick rule <InlineEmoji emoji={emojiMap.sparkles} size={18} />
        </Trans>
      }
      onRequestClose={onRequestClose}
      onOkClick={onOkClick}
      okButtonLabel={<Trans id="dialog-create-rule-okbutton">Create rule</Trans>}
      cancelButtonLabel={<Trans id="dialog-create-rule-cancelbutton">Not now</Trans>}
      okButtonVariant={ButtonVariantsEnum.SECONDARY}
    >
      {dialogContents(props, setHideDialog, hideDialog)}
    </OkCancelDialog>
  );
};

function dialogContents(
  props: CreateTagRuleDialogProps,
  setHideDialog: Dispatch<SetStateAction<boolean>>,
  hideDialog: boolean,
) {
  if (!props.tag || !props.recipients) {
    return null;
  }

  if (!props.recipients[0]) {
    return null;
  }

  const {handle} = props.recipients[0].contact;

  return (
    <>
      <Trans id="dialog-create-rule-movemessages">
        <StyledParagraph>
          Automatically move future messages from <br />
          <StyledBoldSpan>{handle}</StyledBoldSpan> to <StyledBoldSpan>{props.tag.name}</StyledBoldSpan>?
        </StyledParagraph>
      </Trans>
      <StyledCheckBoxWrapperDiv>
        <CheckBox onChange={setHideDialog} isChecked={hideDialog}>
          <Trans id="dialog-create-rule-hidedialog">Don’t show me this again for this tag</Trans>
        </CheckBox>
      </StyledCheckBoxWrapperDiv>
    </>
  );
}
